import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  ButtonGroup,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  Collapse,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard as DashboardIcon,
  TrendingUp,
  Group,
  Notifications,
  NotificationsOff,
  Settings,
  ExitToApp,
  VerifiedUser,
  CalendarToday,
  Menu as MenuIcon,
  School,
  Analytics,
  Psychology,
  ExpandLess,
  ExpandMore,
  KeyboardArrowDown,
  ShowChart,
  AutoGraph,
  SupportAgent,
  AdminPanelSettings,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  ContactMail,
  Store,
  GetApp as DownloadIcon,
  InstallMobile,
} from '@mui/icons-material';
import {
  LinearProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import notificationService from '../services/notificationService';
import pointsService from '../services/pointsService';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { usePWAInstall } from '../hooks/usePWAInstall';

// Navigation structure with grouped items
const getNavigationGroups = (isAdmin: boolean) => {
  const baseGroups = [
    {
      id: 'overview',
      label: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      isStandalone: true,
      priority: 1, // Always show
    },
    {
      id: 'trading',
      label: 'Trading',
      icon: <TrendingUp />,
      priority: 1, // Always show
      items: [
        { 
          label: 'AI Analyses', 
          path: '/ai-analyses', 
          icon: <Psychology />,
          description: 'AI-powered market insights'
        },
        { 
          label: 'Chart', 
          path: '/chart', 
          icon: <TrendingUp />,
          description: 'Advanced trading charts with technical analysis'
        },
        { 
          label: 'Market Intelligence', 
          path: '/market-intelligence', 
          icon: <Analytics />,
          description: 'Real-time market analysis'
        },
        { 
          label: 'Expert Advisors', 
          path: '/expert-advisors', 
          icon: <AutoGraph />,
          description: 'Automated trading systems'
        },
        { 
          label: 'Signals', 
          path: '/signals', 
          icon: <ShowChart />,
          description: 'Premium trading signals'
        },
      ],
    },
    {
      id: 'resources',
      label: 'Resources',
      icon: <School />,
      priority: 2, // Show on larger screens
      items: [
        { 
          label: 'Trading Journal', 
          path: '/trading-journal', 
          icon: <TimelineIcon />,
          description: 'Track and analyze your trades'
        },
        { 
          label: 'Education', 
          path: '/education', 
          icon: <School />,
          description: 'Trading courses & tutorials'
        },
        { 
          label: 'Economic Calendar', 
          path: '/economic-calendar', 
          icon: <CalendarToday />,
          description: 'Important economic events'
        },
        { 
          label: 'Competitions', 
          path: '/competition', 
          icon: <TrophyIcon />,
          description: 'Volume Masters competition rules & leaderboard'
        },
        { 
          label: 'Feedback & Suggestions', 
          path: '/feedback', 
          icon: <SupportAgent />,
          description: 'Share feedback and suggest improvements'
        },
      ],
    },
    {
      id: 'community',
      label: 'Community',
      path: '/community',
      icon: <Group />,
      isStandalone: true,
      priority: 1, // Always show
    },
    {
      id: 'contact',
      label: 'Contact',
      path: '/contact',
      icon: <ContactMail />,
      isStandalone: true,
      priority: 3, // Show on extra large screens only
    },
  ];

  // Add admin group if user is admin
  if (isAdmin) {
    baseGroups.push({
      id: 'admin',
      label: 'Admin',
      icon: <AdminPanelSettings />,
      priority: 2, // Show on larger screens
      items: [
        { 
          label: 'Admin Dashboard', 
          path: '/admin/dashboard', 
          icon: <AdminPanelSettings />,
          description: 'System overview and statistics'
        },
        { 
          label: 'User Management', 
          path: '/admin/users', 
          icon: <PeopleIcon />,
          description: 'Manage user accounts and permissions'
        },
        { 
          label: 'Contact Messages', 
          path: '/admin/contact-messages', 
          icon: <ContactMail />,
          description: 'Manage messages from contact form'
        },
        { 
          label: 'Feedback Management', 
          path: '/admin/feedback', 
          icon: <SupportAgent />,
          description: 'Manage user feedback and suggestions'
        },
        { 
          label: 'Loyalty Config', 
          path: '/admin/loyalty-config', 
          icon: <TrophyIcon />,
          description: 'Configure XM loyalty program'
        },
        { 
          label: 'System Reports', 
          path: '/admin/reports', 
          icon: <Analytics />,
          description: 'User analytics and system reports'
        },
        { 
          label: 'Trading Journal Report', 
          path: '/admin/trading-journal-report', 
          icon: <TimelineIcon />,
          description: 'Trading journal usage and analytics'
        },
        { 
          label: 'Notifications', 
          path: '/admin/notifications', 
          icon: <Notifications />,
          description: 'Send notifications to users'
        },
        { 
          label: 'Product Management', 
          path: '/admin/products', 
          icon: <Store />,
          description: 'Manage downloadable products for loyalty levels'
        },
        { 
          label: 'Download Analytics', 
          path: '/admin/downloads', 
          icon: <DownloadIcon />,
          description: 'Track product downloads and user analytics'
        },
      ],
    });
  }

  return baseGroups;
};

export const AuthenticatedNav: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // Improved mobile detection for better responsiveness on laptop screens
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1280);
  const [screenWidth, setScreenWidth] = React.useState(window.innerWidth);
  
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width < 1280;
      setIsMobile(mobile);
      setScreenWidth(width);
    };
    
    // Initial check
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownAnchor, setDropdownAnchor] = useState<null | HTMLElement>(null);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState<string | null>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationStatus, setNotificationStatus] = useState<'enabled' | 'disabled' | 'unsupported'>('disabled');
  const [userLevel, setUserLevel] = useState<'none' | 'bronze' | 'silver' | 'gold'>('none');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userProgress, setUserProgress] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  
  // PWA Install functionality
  const { isInstallable, isStandalone, install: installPWA } = usePWAInstall();

  // Check notification status
  useEffect(() => {
    const checkNotificationStatus = async () => {
      if (!currentUser) return;
      
      if (!notificationService.isNotificationSupported()) {
        setNotificationStatus('unsupported');
        return;
      }

      try {
        const isEnabled = await notificationService.getUserNotificationPreferences(currentUser.uid);
        const permission = notificationService.getPermissionStatus();
        
        if (isEnabled && permission === 'granted') {
          setNotificationStatus('enabled');
        } else {
          setNotificationStatus('disabled');
        }
      } catch (error) {
        console.error('Error checking notification status:', error);
        setNotificationStatus('disabled');
      }
    };

    checkNotificationStatus();
  }, [currentUser]);

  // Check user level and admin status
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        // Check admin status and get profile data
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        let userData = null;
        if (userDoc.exists()) {
          userData = userDoc.data();
          setIsAdmin(userData.isAdmin === true);
          setUserProfile(userData);
        }

        // Check user level and get detailed progress
        const progress = await pointsService.calculateXmLevel(currentUser.uid, userData);
        setUserProgress(progress);
        
        if (progress?.currentXmLevel && progress.currentXmLevel !== 'none') {
          setUserLevel(progress.currentXmLevel);
        } else {
          setUserLevel('none');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Set to 'none' on error to show "Authenticated" label
        setUserLevel('none');
        setIsAdmin(false);
        setUserProgress(null);
        setUserProfile(null);
      }
    };

    fetchUserData();

    // Listen for level updates from other components
    const handleLevelUpdate = () => {
      fetchUserData();
    };

    window.addEventListener('userLevelUpdated', handleLevelUpdate);
    
    return () => {
      window.removeEventListener('userLevelUpdated', handleLevelUpdate);
    };
  }, [currentUser]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDropdownOpen = (groupId: string, event: React.MouseEvent<HTMLElement>) => {
    setDropdownAnchor(event.currentTarget);
    setOpenDropdown(groupId);
  };

  const handleDropdownClose = () => {
    setOpenDropdown(null);
    setDropdownAnchor(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleDropdownClose();
    setMobileMenuOpen(false);
  };

  const handleMobileGroupToggle = (groupId: string) => {
    setMobileExpandedGroup(mobileExpandedGroup === groupId ? null : groupId);
  };

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleInstallApp = async () => {
    try {
      await installPWA();
    } catch (error) {
      console.error('Failed to install app:', error);
    }
  };

  const getHiddenNavigationGroups = () => {
    if (isMobile) return []; // No hidden groups on mobile
    
    const nonAdminGroups = allNavigationGroups.filter(group => group.id !== 'admin');
    
    if (screenWidth >= 1440) {
      // Large desktop - no hidden groups
      return [];
    } else if (screenWidth >= 1280) {
      // Medium desktop - hide priority 3 items from non-admin groups
      return nonAdminGroups.filter(group => group.priority > 2);
    } else {
      // Small desktop - hide priority 2 and 3 items from non-admin groups
      return nonAdminGroups.filter(group => group.priority > 1);
    }
  };

  const isCurrentPath = (path: string) => location.pathname === path;
  
  const isGroupActive = (group: any) => {
    if (group.isStandalone) {
      return isCurrentPath(group.path);
    }
    return group.items ? group.items.some((item: any) => isCurrentPath(item.path)) : false;
  };

  // Get navigation groups based on admin status and screen size
  const allNavigationGroups = getNavigationGroups(isAdmin);
  
  // Filter navigation groups based on screen size and priority
  const getMainNavigationGroups = () => {
    if (isMobile) return allNavigationGroups; // Mobile menu shows all
    
    // For desktop, we'll show non-admin groups first, then handle admin separately
    const nonAdminGroups = allNavigationGroups.filter(group => group.id !== 'admin');
    
    if (screenWidth >= 1440) {
      // Large desktop - show all non-admin groups
      return nonAdminGroups;
    } else if (screenWidth >= 1280) {
      // Medium desktop (like 13" laptops) - show priority 1 and 2 only
      return nonAdminGroups.filter(group => group.priority <= 2);
    } else {
      // Small desktop - show priority 1 only
      return nonAdminGroups.filter(group => group.priority === 1);
    }
  };
  
  const getAdminGroups = () => {
    return allNavigationGroups.filter(group => group.id === 'admin');
  };
  
  const navigationGroups = getMainNavigationGroups();

  const getLevelDisplay = () => {
    if (userLevel === 'none') return 'Authenticated';
    
    const levelEmojis = {
      bronze: '🥉',
      silver: '🥈', 
      gold: '🥇'
    };
    
    const levelName = userLevel.charAt(0).toUpperCase() + userLevel.slice(1);
    return `${levelEmojis[userLevel]} ${levelName}`;
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return theme.palette.primary.main;
    }
  };

  const getNextLevelInfo = () => {
    const currentLevel = userProgress?.currentXmLevel || 'none';
    const monthlyPoints = userProgress?.monthlyPoints || 0;

    if (currentLevel === 'none') {
      return { nextLevel: 'bronze', target: 0, pointsNeeded: 0 };
    } else if (currentLevel === 'bronze') {
      return { nextLevel: 'silver', target: 500, pointsNeeded: Math.max(0, 500 - monthlyPoints) };
    } else if (currentLevel === 'silver') {
      return { nextLevel: 'gold', target: 1000, pointsNeeded: Math.max(0, 1000 - monthlyPoints) };
    } else {
      return { nextLevel: 'max', target: 1000, pointsNeeded: 0 };
    }
  };

  const getCompletionPercentage = () => {
    const currentLevel = userProgress?.currentXmLevel || 'none';
    const monthlyPoints = userProgress?.monthlyPoints || 0;

    if (currentLevel === 'none') {
      const bronzeProgress = pointsService.getBronzeProgress(userProfile || {});
      return bronzeProgress.completionPercentage;
    } else if (currentLevel === 'bronze') {
      return Math.min(100, Math.round((monthlyPoints / 500) * 100));
    } else if (currentLevel === 'silver') {
      return Math.min(100, Math.round((monthlyPoints / 1000) * 100));
    } else {
      return 100;
    }
  };

  const renderDesktopDropdown = (group: any) => (
    <Popper
      open={openDropdown === group.id}
      anchorEl={dropdownAnchor}
      placement="bottom-start"
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleDropdownClose}>
        <Paper
          sx={{
            backgroundColor: 'rgba(20, 27, 40, 0.98)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            mt: 1,
            minWidth: 280,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <MenuList sx={{ py: 1 }}>
            {group.items && group.items.map((item: any) => (
              <MenuItem
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  py: 1.5,
                  px: 2,
                  mx: 1,
                  borderRadius: 1,
                  backgroundColor: isCurrentPath(item.path) 
                    ? 'rgba(255, 215, 0, 0.15)' 
                    : 'transparent',
                  border: isCurrentPath(item.path) 
                    ? `1px solid ${theme.palette.primary.main}` 
                    : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 36 }}>
                  {item.icon}
                </ListItemIcon>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );

  const renderMobileMenu = (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(15, 21, 32, 0.98)',
          backdropFilter: 'blur(20px)',
          width: 320,
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      {/* User Profile Header */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.primary.main}` }}>
        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <Avatar
            sx={{ 
              width: 52, 
              height: 52,
              backgroundColor: theme.palette.primary.main,
              color: '#000',
              fontWeight: 700,
              mr: 2,
            }}
          >
            {(currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'T').toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
              {currentUser?.displayName?.split(' ')[0] || 'Trader'}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                icon={<VerifiedUser />}
                label={getLevelDisplay()}
                color="primary"
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 215, 0, 0.15)',
                  color: theme.palette.primary.main,
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
              {userProgress && (
                <Chip
                  label={`${getCompletionPercentage()}%`}
                  size="small"
                  sx={{
                    backgroundColor: `${getLevelColor(userLevel)}20`,
                    color: getLevelColor(userLevel),
                    fontWeight: 'bold',
                    fontSize: '0.65rem',
                    height: 20,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Progress Section - Only show if we have user progress data */}
        {userProgress && (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontSize: '0.75rem', fontWeight: 500 }}
              >
                XM Points: {userProgress.xmPoints || 0}
              </Typography>
              {userLevel !== 'gold' && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {userLevel === 'none' ? 'Profile Progress' : `${userProgress.monthlyPoints || 0}/${getNextLevelInfo().target} pts`}
                </Typography>
              )}
            </Box>

            {/* Progress Bar - Hide for gold level */}
            {userLevel !== 'gold' && (
              <LinearProgress
                variant="determinate"
                value={getCompletionPercentage()}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: theme.palette.action.hover,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background: `linear-gradient(90deg, ${getLevelColor(userLevel)}, ${getLevelColor(getNextLevelInfo().nextLevel)})`
                  },
                }}
              />
            )}

            {/* Next Level Info */}
            {userLevel !== 'gold' && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: '0.7rem', 
                  display: 'block', 
                  textAlign: 'center',
                  mt: 0.5 
                }}
              >
                {userLevel === 'none' 
                  ? 'Complete profile for Bronze level'
                  : `${getNextLevelInfo().pointsNeeded} points to ${getNextLevelInfo().nextLevel}`
                }
              </Typography>
            )}

            {/* Max Level Message */}
            {userLevel === 'gold' && (
              <Typography 
                variant="caption" 
                color={getLevelColor(userLevel)}
                sx={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 'bold',
                  display: 'block',
                  textAlign: 'center',
                  mt: 1
                }}
              >
                🏆 Maximum Level Achieved!
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Navigation Groups */}
      <List sx={{ pt: 2, pb: 1 }}>
        {navigationGroups.map((group) => (
          <Box key={group.id}>
            {group.isStandalone ? (
              <ListItem
                onClick={() => handleNavigation(group.path!)}
                sx={{ 
                  cursor: 'pointer',
                  mb: 1,
                  mx: 2,
                  borderRadius: 2,
                  backgroundColor: isCurrentPath(group.path!) 
                    ? 'rgba(255, 215, 0, 0.15)' 
                    : 'transparent',
                  border: isCurrentPath(group.path!) 
                    ? `1px solid ${theme.palette.primary.main}` 
                    : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                }}
              >
                <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                  {group.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={group.label} 
                  sx={{ 
                    '& .MuiListItemText-primary': {
                      fontWeight: isCurrentPath(group.path!) ? 600 : 500,
                      color: isCurrentPath(group.path!) ? theme.palette.primary.main : 'inherit',
                    }
                  }}
                />
              </ListItem>
            ) : (
              <>
                <ListItem
                  onClick={() => handleMobileGroupToggle(group.id)}
                  sx={{ 
                    cursor: 'pointer',
                    mx: 2,
                    borderRadius: 2,
                    backgroundColor: isGroupActive(group) 
                      ? 'rgba(255, 215, 0, 0.1)' 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                    {group.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={group.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isGroupActive(group) ? 600 : 500,
                        color: isGroupActive(group) ? theme.palette.primary.main : 'inherit',
                      }
                    }}
                  />
                  {mobileExpandedGroup === group.id ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={mobileExpandedGroup === group.id} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items && group.items.map((item: any) => (
                      <ListItem
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        sx={{ 
                          cursor: 'pointer',
                          pl: 6,
                          pr: 2,
                          mx: 2,
                          borderRadius: 2,
                          backgroundColor: isCurrentPath(item.path) 
                            ? 'rgba(255, 215, 0, 0.15)' 
                            : 'transparent',
                          border: isCurrentPath(item.path) 
                            ? `1px solid ${theme.palette.primary.main}` 
                            : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 215, 0, 0.1)',
                            border: `1px solid ${theme.palette.primary.main}`,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 36 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.label}
                          secondary={item.description}
                          sx={{ 
                            '& .MuiListItemText-primary': {
                              fontWeight: isCurrentPath(item.path) ? 600 : 400,
                              color: isCurrentPath(item.path) ? theme.palette.primary.main : 'inherit',
                              fontSize: '0.9rem',
                            },
                            '& .MuiListItemText-secondary': {
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
          </Box>
        ))}
        
        <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 215, 0, 0.2)' }} />
        
        {/* Profile Actions */}
        <ListItem
          onClick={() => handleNavigation('/profile')}
          sx={{ 
            cursor: 'pointer',
            mx: 2,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'rgba(255, 215, 0, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </ListItem>
        
        <ListItem
          onClick={() => handleNavigation('/settings')}
          sx={{ 
            cursor: 'pointer',
            mx: 2,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'rgba(255, 215, 0, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <Settings />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>

        {/* Admin Quick Access */}
        {isAdmin && (
          <ListItem
            onClick={() => handleNavigation('/admin/dashboard')}
            sx={{ 
              cursor: 'pointer',
              mx: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 165, 0, 0.1)',
              border: '1px solid rgba(255, 165, 0, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 165, 0, 0.2)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'warning.main', minWidth: 40 }}>
              <AdminPanelSettings />
            </ListItemIcon>
            <ListItemText 
              primary="Admin Portal" 
              sx={{ 
                '& .MuiListItemText-primary': {
                  fontWeight: 600,
                  color: 'warning.main',
                }
              }}
            />
          </ListItem>
        )}
        
        <ListItem
          onClick={handleLogout}
          sx={{ 
            cursor: 'pointer',
            mx: 2,
            borderRadius: 2,
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Drawer>
  );

  const renderProfileMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(20, 27, 40, 0.98)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${theme.palette.primary.main}`,
          minWidth: 200,
          borderRadius: 2,
        },
      }}
    >
      <MenuItem 
        onClick={() => {
          navigate('/profile');
          handleMenuClose();
        }}
        sx={{ py: 1.5, px: 2 }}
      >
        <AccountCircle sx={{ mr: 2, color: theme.palette.primary.main }} />
        <Typography variant="body2">Profile</Typography>
      </MenuItem>
      <MenuItem 
        onClick={() => {
          navigate('/settings');
          handleMenuClose();
        }}
        sx={{ py: 1.5, px: 2 }}
      >
        <Settings sx={{ mr: 2, color: theme.palette.primary.main }} />
        <Typography variant="body2">Settings</Typography>
      </MenuItem>
      {isInstallable && !isStandalone && (
        <MenuItem 
          onClick={() => {
            handleInstallApp();
            handleMenuClose();
          }}
          sx={{ py: 1.5, px: 2 }}
        >
          <InstallMobile sx={{ mr: 2, color: theme.palette.primary.main }} />
          <Typography variant="body2">Install App</Typography>
        </MenuItem>
      )}
      {isAdmin && (
        <MenuItem 
          onClick={() => {
            navigate('/admin/dashboard');
            handleMenuClose();
          }}
          sx={{ 
            py: 1.5, 
            px: 2,
            backgroundColor: 'rgba(255, 165, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 165, 0, 0.2)',
            },
          }}
        >
          <AdminPanelSettings sx={{ mr: 2, color: 'warning.main' }} />
          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
            Admin Portal
          </Typography>
        </MenuItem>
      )}
      <Divider sx={{ backgroundColor: 'rgba(255, 215, 0, 0.2)' }} />
      <MenuItem 
        onClick={handleLogout}
        sx={{ 
          py: 1.5,
          px: 2,
          color: 'error.main',
          '&:hover': {
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
          },
        }}
      >
        <ExitToApp sx={{ mr: 2 }} />
        <Typography variant="body2">Logout</Typography>
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          backgroundColor: 'rgba(15, 21, 32, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${theme.palette.primary.main}`,
          boxShadow: '0 4px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 4 }, minHeight: '72px' }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ 
                mr: 2,
                minWidth: '48px',
                minHeight: '48px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
          )}

          {/* Logo Section */}
          <Box display="flex" alignItems="center" sx={{ flexGrow: isMobile ? 1 : 0 }}>
            <Box
              component="img"
              src="/images/Logos/T1 Logo White Banner Transp2.png"
              alt="T1 FX Team Logo"
              sx={{
                height: { xs: 36, md: 44 },
                width: 'auto',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  filter: 'drop-shadow(0 0 12px rgba(255, 215, 0, 0.3))',
                },
              }}
              onClick={() => navigate('/dashboard')}
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', ml: { md: 4, lg: 6 } }}>
              {navigationGroups.map((group) => (
                <Box key={group.id} sx={{ mr: { md: 0.5, lg: 1 } }}>
                  {group.isStandalone ? (
                    <Button
                      color="inherit"
                      onClick={() => navigate(group.path!)}
                      startIcon={group.icon}
                      sx={{
                        px: { md: 2, lg: 3 },
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: { md: '0.8rem', lg: '0.9rem' },
                        borderRadius: 2,
                        backgroundColor: isCurrentPath(group.path!) 
                          ? 'rgba(255, 215, 0, 0.15)' 
                          : 'transparent',
                        border: isCurrentPath(group.path!) 
                          ? `1px solid ${theme.palette.primary.main}` 
                          : '1px solid transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {group.label}
                    </Button>
                  ) : (
                    <>
                      <Button
                        color="inherit"
                        endIcon={<KeyboardArrowDown />}
                        startIcon={group.icon}
                        onClick={(e) => handleDropdownOpen(group.id, e)}
                        sx={{
                          px: { md: 2, lg: 3 },
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: { md: '0.8rem', lg: '0.9rem' },
                          borderRadius: 2,
                          backgroundColor: isGroupActive(group) 
                            ? 'rgba(255, 215, 0, 0.15)' 
                            : 'transparent',
                          border: isGroupActive(group) 
                            ? `1px solid ${theme.palette.primary.main}` 
                            : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 215, 0, 0.1)',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {group.label}
                      </Button>
                      {renderDesktopDropdown(group)}
                    </>
                  )}
                </Box>
                              ))}
              
              {/* More Menu for Hidden Items */}
              {!isMobile && getHiddenNavigationGroups().length > 0 && (
                <Box sx={{ mr: { md: 0.5, lg: 1 } }}>
                  <Button
                    color="inherit"
                    endIcon={<KeyboardArrowDown />}
                    onClick={handleMoreMenuOpen}
                    sx={{
                      px: { md: 2, lg: 3 },
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: { md: '0.8rem', lg: '0.9rem' },
                      borderRadius: 2,
                      backgroundColor: Boolean(moreMenuAnchor) 
                        ? 'rgba(255, 215, 0, 0.15)' 
                        : 'transparent',
                      border: Boolean(moreMenuAnchor) 
                        ? `1px solid ${theme.palette.primary.main}` 
                        : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        border: `1px solid ${theme.palette.primary.main}`,
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    ...
                  </Button>
                  
                  <Menu
                    anchorEl={moreMenuAnchor}
                    open={Boolean(moreMenuAnchor)}
                    onClose={handleMoreMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: {
                        backgroundColor: 'rgba(20, 27, 40, 0.98)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        minWidth: 200,
                      },
                    }}
                  >
                    {getHiddenNavigationGroups().map((group) => (
                      <div key={group.id}>
                        {group.isStandalone ? (
                          <MenuItem
                            onClick={() => {
                              navigate(group.path!);
                              handleMoreMenuClose();
                            }}
                            sx={{
                              py: 1.5,
                              px: 2,
                              backgroundColor: isCurrentPath(group.path!) 
                                ? 'rgba(255, 215, 0, 0.1)' 
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 215, 0, 0.15)',
                              },
                            }}
                          >
                            <Box sx={{ mr: 2, color: theme.palette.primary.main }}>
                              {group.icon}
                            </Box>
                            <Typography variant="body2">{group.label}</Typography>
                          </MenuItem>
                        ) : (
                          group.items?.map((item) => (
                            <MenuItem
                              key={item.path}
                              onClick={() => {
                                navigate(item.path);
                                handleMoreMenuClose();
                              }}
                              sx={{
                                py: 1.5,
                                px: 2,
                                backgroundColor: isCurrentPath(item.path) 
                                  ? 'rgba(255, 215, 0, 0.1)' 
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 215, 0, 0.15)',
                                },
                              }}
                            >
                              <Box sx={{ mr: 2, color: theme.palette.primary.main }}>
                                {item.icon}
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {item.label}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    display: 'block',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {item.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </div>
                    ))}
                  </Menu>
                </Box>
              )}
              
              {/* Admin Navigation */}
              {!isMobile && getAdminGroups().map((group) => (
                <Box key={group.id} sx={{ mr: { md: 0.5, lg: 1 } }}>
                  {group.isStandalone ? (
                    <Button
                      color="inherit"
                      onClick={() => navigate(group.path!)}
                      startIcon={group.icon}
                      sx={{
                        px: { md: 2, lg: 3 },
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: { md: '0.8rem', lg: '0.9rem' },
                        borderRadius: 2,
                        backgroundColor: isCurrentPath(group.path!) 
                          ? 'rgba(255, 215, 0, 0.15)' 
                          : 'transparent',
                        border: isCurrentPath(group.path!) 
                          ? `1px solid ${theme.palette.primary.main}` 
                          : '1px solid transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {group.label}
                    </Button>
                  ) : (
                    <>
                      <Button
                        color="inherit"
                        endIcon={<KeyboardArrowDown />}
                        startIcon={group.icon}
                        onClick={(e) => handleDropdownOpen(group.id, e)}
                        sx={{
                          px: { md: 2, lg: 3 },
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: { md: '0.8rem', lg: '0.9rem' },
                          borderRadius: 2,
                          backgroundColor: isGroupActive(group) 
                            ? 'rgba(255, 215, 0, 0.15)' 
                            : 'transparent',
                          border: isGroupActive(group) 
                            ? `1px solid ${theme.palette.primary.main}` 
                            : '1px solid transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 215, 0, 0.1)',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {group.label}
                      </Button>
                      {renderDesktopDropdown(group)}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { md: 1, lg: 2 } }}>
            {/* User Status Chip - Large Desktop Only */}
            <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center' }}>
              <Chip
                icon={<VerifiedUser />}
                label={getLevelDisplay()}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  '& .MuiChip-icon': {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            </Box>

            {/* Welcome Message - Extra Large Desktop Only */}
            <Box sx={{ display: { xs: 'none', '2xl': 'flex' }, flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography
                variant="caption"
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  lineHeight: 1,
                }}
              >
                Welcome back,
              </Typography>
              <Typography
                variant="body2"
                sx={{ 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  fontSize: '0.85rem',
                  lineHeight: 1.2,
                }}
              >
                {currentUser?.displayName?.split(' ')[0] || 'Trader'}
              </Typography>
            </Box>

            {/* Notifications */}
            <Tooltip title={
              notificationStatus === 'enabled' ? 'Notifications enabled' :
              notificationStatus === 'disabled' ? 'Notifications disabled' :
              'Notifications not supported'
            }>
              <IconButton
                color="inherit"
                onClick={() => navigate('/community')}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  },
                }}
              >
                {notificationStatus === 'enabled' ? (
                  <Badge badgeContent="" color="success" variant="dot">
                    <Notifications />
                  </Badge>
                ) : (
                  <NotificationsOff color={notificationStatus === 'unsupported' ? 'disabled' : 'inherit'} />
                )}
              </IconButton>
            </Tooltip>

            {/* PWA Install Button */}
            {isInstallable && !isStandalone && (
              <Tooltip title="Install App">
                <IconButton
                  color="inherit"
                  onClick={handleInstallApp}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  <InstallMobile />
                </IconButton>
              </Tooltip>
            )}

            {/* Profile Avatar */}
            <Tooltip title="Account Menu">
              <IconButton
                size="large"
                edge="end"
                aria-label="account menu"
                onClick={handleProfileMenuOpen}
                sx={{
                  border: `2px solid ${theme.palette.primary.main}`,
                  padding: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {currentUser?.photoURL ? (
                  <Avatar
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || 'User'}
                    sx={{ width: 40, height: 40 }}
                  />
                ) : (
                  <Avatar
                    sx={{ 
                      width: 40, 
                      height: 40,
                      backgroundColor: theme.palette.primary.main,
                      color: '#000',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                    }}
                  >
                    {(currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'T').toUpperCase()}
                  </Avatar>
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderProfileMenu}
    </>
  );
}; 